import { useQuery } from '@tanstack/react-query';

import { Card, CardContent } from '@/components/core/card';
import { DetailList } from '@/components/detail-list';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import type { SimulationScenarioDTO } from '@/gateways/impl/SimulationGateway';
import { fetchAllPages, formatDateTime } from '@/shared/helpers';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { simulationScenarioRunStatusType } from '@/shared/status-maps';
import { simulationScenarioRunStatusTitle } from '@/shared/title-maps';

type Props = {
  scenario?: SimulationScenarioDTO;
  isLoading?: boolean;
};

export function ScenarioConfigSection({ scenario, isLoading }: Props) {
  const { accountsGateway, shippingGateway } = useGateways();
  const accountId = scenario?.configSnapshot.accountId;
  const locationId = scenario?.configSnapshot.locationId;
  const carrierServiceIds = scenario?.configSnapshot.carrierServiceIds ?? [];

  const { data: account } = useQuery({
    queryKey: ['accounts', accountId],
    queryFn: ({ signal }) => accountsGateway.getAccount(accountId!, { signal }),
    enabled: Boolean(accountId),
  });

  const { data: location } = useQuery({
    queryKey: ['locations', locationId],
    queryFn: ({ signal }) => accountsGateway.getLocation(locationId!, { signal }),
    enabled: Boolean(locationId),
  });

  const { data: carrierNames = [] } = useQueryWithInput({
    parentKey: 'carrierServices',
    input: { scope: 'simulation-scenario-config' },
    query: (_input, { signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarrierServices({ cursor, take }, { signal }),
      ),
    select: (carrierServices) =>
      carrierServices
        .filter((c) => c.id && carrierServiceIds.includes(c.id))
        .map((c) => c.name)
        .filter((name): name is string => Boolean(name)),
    enabled: Boolean(scenario),
  });

  return (
    <Card>
      <CardContent>
        <DetailList
          title="Configuration"
          isLoading={isLoading}
          items={[
            { label: 'Account', value: account?.name ?? accountId },
            { label: 'Location', value: location?.name ?? locationId },
            {
              label: 'Carrier services',
              value:
                carrierNames.length > 0 ? carrierNames.join(', ') : carrierServiceIds.join(', '),
            },
            { label: 'Created by', value: scenario?.createdBy },
            {
              label: 'Created',
              value: scenario?.createdAt ? formatDateTime(scenario.createdAt) : undefined,
            },
            {
              label: 'Started',
              value: scenario?.startedAt ? formatDateTime(scenario.startedAt) : undefined,
            },
            {
              label: 'Completed',
              value: scenario?.completedAt ? formatDateTime(scenario.completedAt) : undefined,
            },
            {
              label: 'Run status',
              value: scenario ? (
                <StatusBadge
                  status={scenario.runStatus}
                  type={simulationScenarioRunStatusType}
                  title={simulationScenarioRunStatusTitle}
                />
              ) : undefined,
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}
